/**
 * analysisHelpers.ts
 * Pure functions extracted from AnalysisWorker.ts.
 * Syllable counting uses the `syllable` npm package as the primary engine; a small
 * domain-exception dictionary overrides it for intranet/HR/British vocabulary.
 * No @xenova dependency.
 */
import { syllable } from 'syllable';

/**
 * Subset of CopilotAnalysisConfig that the analysis helpers need.
 * Kept here (not imported from CoreSettingsService) so this file remains dependency-free.
 */
export interface AnalysisConfig {
  readabilityClampMin?: number; // default 40
  readabilityClampMax?: number; // default 90
  readinessWeights?: {
    structure: number; // default 0.5
    readability: number; // default 0.3
    base: number; // default 20
  };
  layoutThresholds?: {
    warning: number; // default 0.19
    fail: number; // default 0.25
  };
  keywordStuffing?: {
    ttrThreshold: number; // default 0.4
    ngramTopFrequency: number; // default 5  (max times top bigram may appear)
  };
}

/** Safe defaults — mirror DEFAULT_COPILOT_CONFIG from CoreSettingsService */
const DEFAULT_ANALYSIS_CONFIG: Required<AnalysisConfig> = {
  readabilityClampMin: 40,
  readabilityClampMax: 90,
  readinessWeights: { structure: 0.5, readability: 0.3, base: 20 },
  layoutThresholds: { warning: 0.19, fail: 0.25 },
  keywordStuffing: { ttrThreshold: 0.4, ngramTopFrequency: 5 },
};

export interface ReadabilityDetail {
  sentenceCount: number;
  wordCount: number;
  syllableCount: number;
  wordsPerSentence: number;
  syllablesPerWord: number;
  freRaw: number;
  score: number;
  formula: string;
  diagnostics?: {
    freClamped: number;
    excludedTokenCount: number;
    excludedTokenExamples: string[];
    topComplexWords: Array<{
      word: string;
      count: number;
      syllables: number;
    }>;
  };
}

export interface StructureDetail {
  h1Count: number;
  h2Count: number;
  h3Count: number;
  listItemCount: number;
  tableCount: number;
  h1Score: number;
  h2Score: number;
  h3Score: number;
  listScore: number;
  tableScore: number;

  // Penalties
  skippedLevelCount: number;
  emptyHeaderCount: number;
  genericLinkCount: number;
  missingThTableCount: number;

  skippedLevelPenalty: number;
  emptyHeaderPenalty: number;
  genericLinkPenalty: number;
  missingThTablePenalty: number;

  positiveTotal: number;
  penaltyTotal: number;

  totalRaw: number;
  score: number;
}

export interface IntentDetail {
  matchedRule: string | null;
  matchedKeyword: string | null;
  intent: string;
  /** Page title that was prepended 3× for weighting (empty string = no title available) */
  titleUsed: string;
}

// ─────────────────── People-First Scanner ────────────────────

export type PeopleFirstPenalty = 'Critical' | 'High' | 'Medium' | 'Low';

export interface PeopleFirstFinding {
  /** Whether this specific category was triggered */
  triggered: boolean;
  count: number;
  /** Threshold that triggers this category (null = any occurrence) */
  threshold: number | null;
  penalty: PeopleFirstPenalty;
  recommendation: string;
  examples: string[];
}

export interface PeopleFirstDetail {
  /** Fails if any Critical or High finding is triggered */
  passes: boolean;
  directives: PeopleFirstFinding; // command-style LLM language
  hiddenText: PeopleFirstFinding; // text hidden from human view
  unicodeJunk: PeopleFirstFinding; // zero-width / invisible chars
  delimiters: PeopleFirstFinding; // machine-parsing delimiters
  aiAttributes: PeopleFirstFinding; // data-ai-* / data-llm-* attributes
}

export interface AnalysisReportDetail {
  writeForPeopleFirst: boolean;
  peopleFirstDetail: PeopleFirstDetail;
  complexLayoutPenalized: boolean;
  /** SharePoint-aware 4-level tag-density rating */
  complexLayoutLevel: 'Excellent' | 'Standard' | 'Warning' | 'Fail';
  shortSections: boolean;
  clearHeadings: boolean;
  metadataPresent: boolean;
  imagesHaveAltText: boolean;
  hasStrongSummary: boolean;
  noKeywordStuffing: boolean;
  metrics: {
    tagCount: number;
    textLength: number;
    tagDensity: number;
    oversizeChunks: number;
    h2h3Count: number;
    imageCount: number;
    firstParaWordCount: number;
    /** Legacy single-word top keyword */
    topKeyword: string;
    topKeywordFrequency: number;
    /** Type-Token Ratio: unique words / total words (0–1, lower = more repetitive) */
    ttr: number;
    /** Most repeated 2–3 word phrase and how often it appears per 100 bigrams */
    topNgram: string;
    topNgramFrequency: number;
    /** Which signal(s) triggered the stuffing flag */
    stuffingSignals: string[];
  };
}

export interface FullAnalysisDetail {
  readability: ReadabilityDetail;
  structure: StructureDetail;
  intent: IntentDetail;
  readiness: number;
  readinessFormula: string;
  report: AnalysisReportDetail;
}

// ─────────────────── Readability ────────────────────

const READABILITY_SYLLABLE_EXCEPTIONS: Record<string, number> = {
  // Brand / intranet compound words (not reliably in standard corpora)
  email: 2,
  microsoft: 3,
  sharepoint: 2,
  timesheet: 2,
  workflow: 2,
  workplace: 2,
  stakeholder: 3,
  password: 2,

  // Tricky silent-letter words
  aisle: 1,
  niche: 1,
  queue: 1,
  regime: 2,
  league: 1,
  plague: 1,

  // British/Australian English spellings (-isation suffix)
  organisation: 5,
  organisations: 5,
  prioritisation: 6,
  specialisation: 5,
  utilisation: 5,
  standardisation: 6,
  centralisation: 6,

  // Long HR/workplace vocab — `syllable` pkg occasionally miscounts -tion/-ity
  accountability: 6,
  administration: 5,
  administrative: 5,
  availability: 6,
  capability: 5,
  collaboration: 5,
  communication: 5,
  confidentiality: 6,
  documentation: 5,
  eligibility: 5,
  implementation: 5,
  justification: 5,
  notification: 5,
  prioritization: 6,
  recommendation: 5,
  rehabilitation: 6,
  responsibility: 6,
  superannuation: 6,
  verification: 5,
};

function normalizeReadabilityToken(token: string): string {
  return token
    .toLowerCase()
    .replace(/[’']/g, "'")
    .replace(/^[^a-z]+|[^a-z]+$/gi, '');
}

function isReadableWordToken(token: string): boolean {
  if (!token || token.length < 2) return false;
  if (!/[a-z]/i.test(token)) return false;
  if (/https?:\/\//i.test(token)) return false;
  if (/^[\w.+-]+@[\w.-]+\.[a-z]{2,}$/i.test(token)) return false;
  if (/\d/.test(token)) return false;
  return true;
}

function tokenizeReadableWords(text: string): {
  words: string[];
  excludedCount: number;
  excludedExamples: string[];
} {
  const rawTokens = (text || '').split(/\s+/).filter(Boolean);
  const words: string[] = [];
  let excludedCount = 0;
  const excludedExamples: string[] = [];

  for (const raw of rawTokens) {
    const token = normalizeReadabilityToken(raw);
    if (!isReadableWordToken(token)) {
      excludedCount++;
      if (excludedExamples.length < 8 && raw.trim()) excludedExamples.push(raw);
      continue;
    }
    words.push(token);
  }

  return { words, excludedCount, excludedExamples };
}

function topComplexWordStats(
  words: string[],
): Array<{ word: string; count: number; syllables: number }> {
  const counts: Record<string, { count: number; syllables: number }> = {};
  for (const w of words) {
    const s = estimateSyllables(w);
    if (s < 3) continue;
    if (!counts[w]) counts[w] = { count: 0, syllables: s };
    counts[w].count++;
  }

  return Object.entries(counts)
    .map(([word, info]) => ({
      word,
      count: info.count,
      syllables: info.syllables,
    }))
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      if (b.syllables !== a.syllables) return b.syllables - a.syllables;
      return a.word.localeCompare(b.word);
    })
    .slice(0, 8);
}

function estimateSyllables(word: string): number {
  const w = normalizeReadabilityToken(word).replace(/[^a-z]/g, '');
  if (!w) return 0;
  // Domain-specific overrides: intranet/HR vocab, brand names, British spellings
  const override = READABILITY_SYLLABLE_EXCEPTIONS[w];
  if (typeof override === 'number') return override;
  // Delegate to industry-standard algorithm
  return syllable(w);
}

export function computeReadabilityDetailed(
  text: string,
  config?: AnalysisConfig,
): ReadabilityDetail {
  const clampMin =
    config?.readabilityClampMin ?? DEFAULT_ANALYSIS_CONFIG.readabilityClampMin;
  const clampMax =
    config?.readabilityClampMax ?? DEFAULT_ANALYSIS_CONFIG.readabilityClampMax;
  const raw = (text ?? '').trim();
  const clean = raw.replace(/\s+/g, ' ').trim();
  if (!clean) {
    return {
      sentenceCount: 0,
      wordCount: 0,
      syllableCount: 0,
      wordsPerSentence: 0,
      syllablesPerWord: 0,
      freRaw: 60,
      score: 60,
      formula: 'No text content — returned fallback 60',
    };
  }

  // Include line breaks and list-like boundaries so structured intranet pages
  // are not treated as one long sentence block.
  const sentenceSource = raw
    .replace(/\r\n/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/(?:^|\n)\s*(?:[•\-*]|\d+[.)])\s+/g, '\n')
    .replace(/:\s*\n/g, '.\n');
  const sentenceFragments = sentenceSource
    .split(/(?:[.!?]+(?=\s|$)|[;:](?=\s+[A-Z]|\s*$)|\n+)/)
    .filter((s) => s.trim().length > 0);
  const sentences = sentenceFragments.length || 1;
  const tokenization = tokenizeReadableWords(clean);
  const wordsArr = tokenization.words;
  const words = wordsArr.length || 1;
  const syllables = wordsArr.reduce((sum, w) => sum + estimateSyllables(w), 0);
  const complexWords = topComplexWordStats(wordsArr);

  const wps = words / sentences;
  const spw = syllables / words;
  const freRaw = 206.835 - 1.015 * wps - 84.6 * spw;
  const freClamped = Math.max(clampMin, Math.min(clampMax, freRaw));
  const score = Math.round(freClamped);

  return {
    sentenceCount: sentences,
    wordCount: words,
    syllableCount: syllables,
    wordsPerSentence: Math.round(wps * 10) / 10,
    syllablesPerWord: Math.round(spw * 100) / 100,
    freRaw: Math.round(freRaw * 10) / 10,
    score,
    formula: `206.835 − 1.015×(${words}/${sentences}) − 84.6×(${syllables}/${words}) = ${Math.round(freRaw * 10) / 10} → clamped [${clampMin}–${clampMax}] to ${Math.round(freClamped * 10) / 10} → rounded to ${score}`,
    diagnostics: {
      freClamped: Math.round(freClamped * 10) / 10,
      excludedTokenCount: tokenization.excludedCount,
      excludedTokenExamples: tokenization.excludedExamples,
      topComplexWords: complexWords,
    },
  };
}

// ─────────────────── Structure ────────────────────

/**
 * Extract ARIA heading counts from HTML by level.
 *
 * SharePoint Modern uses `<div role="heading" aria-level="2">` for section
 * headings in layout/column web parts. These are valid accessible headings
 * but invisible to a naive `<h2>` regex scan.
 *
 * Supports both attribute orderings:
 *   role="heading" aria-level="2"   (most common)
 *   aria-level="2" role="heading"   (also valid)
 */
function countAriaHeadingsByLevel(html: string): {
  h1: number;
  h2: number;
  h3: number;
} {
  const hits = [
    ...(html.match(/role=["']heading["'][^>]*aria-level=["'](\d)["']/gi) ?? []),
    ...(html.match(/aria-level=["'](\d)["'][^>]*role=["']heading["']/gi) ?? []),
  ];
  let h1 = 0,
    h2 = 0,
    h3 = 0;
  for (const hit of hits) {
    const lvl = parseInt(
      (hit.match(/aria-level=["'](\d)["']/i) ?? [])[1] || '0',
    );
    if (lvl === 1) h1++;
    else if (lvl === 2) h2++;
    else if (lvl === 3) h3++;
  }
  return { h1, h2, h3 };
}

/**
 * @param html      CanvasContent1 HTML from SharePoint.
 * @param pageTitle Optional: the SP list item Title field.
 *
 * SharePoint NEVER includes the page title banner in CanvasContent1 — it is
 * rendered by a dedicated Title web part outside the canvas. Every modern
 * SharePoint page therefore appears to have zero H1 tags when only the canvas
 * HTML is parsed.
 *
 * Fix: if a non-empty pageTitle is supplied and no native or ARIA H1 is found
 * in the canvas HTML, count the title as the implicit H1 that SharePoint
 * provides visually and semantically (via the title banner's own ARIA markup).
 */
export function analyzeHtmlStructureDetailed(
  html: string,
  pageTitle?: string,
): StructureDetail {
  if (!html && !pageTitle?.trim()) {
    return {
      h1Count: 0,
      h2Count: 0,
      h3Count: 0,
      listItemCount: 0,
      tableCount: 0,
      h1Score: 0,
      h2Score: 0,
      h3Score: 0,
      listScore: 0,
      tableScore: 0,
      skippedLevelCount: 0,
      emptyHeaderCount: 0,
      genericLinkCount: 0,
      missingThTableCount: 0,
      skippedLevelPenalty: 0,
      emptyHeaderPenalty: 0,
      genericLinkPenalty: 0,
      missingThTablePenalty: 0,
      positiveTotal: 0,
      penaltyTotal: 0,
      totalRaw: 0,
      score: 0,
    };
  }

  // ── Native semantic headings ──────────────────────────────
  let h1Count = (html.match(/<h1[^>]*>/gi) ?? []).length;
  let h2Count = (html.match(/<h2[^>]*>/gi) ?? []).length;
  let h3Count = (html.match(/<h3[^>]*>/gi) ?? []).length;

  // ── ARIA headings (SharePoint layout web parts) ────────────
  const aria = countAriaHeadingsByLevel(html);
  h1Count += aria.h1;
  h2Count += aria.h2;
  h3Count += aria.h3;

  // ── Page Title → implicit H1
  if (pageTitle?.trim() && h1Count === 0) h1Count = 1;

  // ── Lists (only count if inside ul/ol, and ignore single-item lists) ──
  const listBlocks = html.match(/<(ul|ol)\b[^>]*>([\s\S]*?)<\/\1>/gi) || [];
  let listItemCount = 0;
  for (const block of listBlocks) {
    const lis = (block.match(/<li\b[^>]*>/gi) || []).length;
    if (lis > 1) listItemCount += lis;
  }

  // ── Tables ──
  const tables = html.match(/<table\b[^>]*>([\s\S]*?)<\/table>/gi) || [];
  const tableCount = tables.length;
  let missingThTableCount = 0;
  for (const t of tables) {
    if (!/<th\b[^>]*>/i.test(t)) missingThTableCount++;
  }

  // ── Penalties: Skipped Level (H3 before H2) ──
  let skippedLevelCount = 0;
  const headingMatches = [
    ...html.matchAll(
      /<h([1-6])\b[^>]*>|role=["']heading["'][^>]*aria-level=["']([1-6])["']|aria-level=["']([1-6])["'][^>]*role=["']heading["']/gi,
    ),
  ];
  let seenH2 = false;
  for (const m of headingMatches) {
    const level = parseInt(m[1] || m[2] || m[3] || '0');
    if (level === 2) seenH2 = true;
    if (level === 3 && !seenH2) {
      skippedLevelCount = 1; // binary penalty for the page
      break;
    }
  }

  // ── Penalties: Empty Headers ──
  // Match native <hX></hX> or ARIA heading divs that are empty or just whitespace
  const headerTags = [
    ...(html.match(/<h[1-6]\b[^>]*>([\s\S]*?)<\/h[1-6]>/gi) || []),
    ...(html.match(
      /<div[^>]*(?:role=["']heading["'][^>]*aria-level=["'][1-6]["']|aria-level=["'][1-6]["'][^>]*role=["']heading["'])[^>]*>([\s\S]*?)<\/div>/gi,
    ) || []),
  ];
  let emptyHeaderCount = 0;
  for (const tag of headerTags) {
    const inner = tag
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/gi, '')
      .trim();
    if (inner.length === 0) emptyHeaderCount++;
  }

  // ── Penalties: Generic Links (only inside paragraphs) ──
  let genericLinkCount = 0;
  const pBlocks = html.match(/<p\b[^>]*>([\s\S]*?)<\/p>/gi) || [];
  const genericRe = /<a\b[^>]*>([\s\S]*?)<\/a>/gi;
  const genericPhrases = new Set([
    'click here',
    'read more',
    'learn more',
    'here',
  ]);
  for (const p of pBlocks) {
    let match;
    while ((match = genericRe.exec(p)) !== null) {
      const linkText = match[1]
        .replace(/<[^>]+>/g, '')
        .trim()
        .toLowerCase();
      if (genericPhrases.has(linkText)) genericLinkCount++;
    }
  }

  // ── Scoring Math ──
  const h1Score = h1Count > 0 ? 20 : 0;
  const h2Score = Math.min(36, h2Count * 12);
  const h3Score = Math.min(20, h3Count * 5);
  const listScore = Math.min(20, listItemCount * 2);
  const tableScore = Math.min(10, tableCount * 5);

  const positiveTotal = h1Score + h2Score + h3Score + listScore + tableScore;
  const clampedPositive = Math.min(100, positiveTotal);

  const skippedLevelPenalty = skippedLevelCount > 0 ? -15 : 0;
  const emptyHeaderPenalty = Math.max(-20, emptyHeaderCount * -10);
  const genericLinkPenalty = Math.max(-15, genericLinkCount * -5);
  const missingThTablePenalty = Math.max(-20, missingThTableCount * -10);

  const penaltyTotal =
    skippedLevelPenalty +
    emptyHeaderPenalty +
    genericLinkPenalty +
    missingThTablePenalty;
  const totalRaw = clampedPositive + penaltyTotal;
  const score = Math.max(20, Math.min(100, totalRaw));

  return {
    h1Count,
    h2Count,
    h3Count,
    listItemCount,
    tableCount,
    h1Score,
    h2Score,
    h3Score,
    listScore,
    tableScore,
    skippedLevelCount,
    emptyHeaderCount,
    genericLinkCount,
    missingThTableCount,
    skippedLevelPenalty,
    emptyHeaderPenalty,
    genericLinkPenalty,
    missingThTablePenalty,
    positiveTotal: clampedPositive,
    penaltyTotal,
    totalRaw,
    score,
  };
}

// ─────────────────── Intent ────────────────────

const INTENT_DEFS: Array<{
  intent: string;
  patterns: Array<{ label: string; regex: RegExp }>;
}> = [
  {
    intent: 'Safety',
    patterns: [
      { label: 'safety', regex: /\bsafety\b/i },
      { label: 'hazard', regex: /\bhazard\b/i },
      { label: 'accident', regex: /\baccident\b/i },
      { label: 'emergency', regex: /\bemergency\b/i },
      { label: 'evacuation', regex: /\bevacuat(e|ion|ing)\b/i },
      { label: 'mental health', regex: /\bmental\s+health\b/i },
      { label: 'first aid', regex: /\bfirst\s+aid\b/i },
      { label: 'fire', regex: /\bfire\b/i },
      { label: 'wellness', regex: /\bwellness\b/i },
      { label: 'well-being', regex: /\bwell-?being\b/i },
      { label: 'injury', regex: /\binjur(y|ies)\b/i },
    ],
  },
  {
    intent: 'Procedural',
    patterns: [
      { label: 'process', regex: /\bprocess\b/i },
      { label: 'procedure', regex: /\bprocedure\b/i },
      { label: 'step', regex: /\bsteps?\b/i },
      { label: 'how to', regex: /\bhow\s+to\b/i },
      { label: 'how do i', regex: /\bhow\s+do\s+i\b/i },
      { label: 'guide', regex: /\bguide\b/i },
      { label: 'submitting', regex: /\bsubmitting\b/i },
      { label: 'claiming', regex: /\bclaiming\b/i },
      { label: 'first step', regex: /\bfirst\s+step\b/i },
      { label: 'next step', regex: /\bnext\s+step\b/i },
      { label: 'ensure', regex: /\bensure\b/i },
      { label: 'follow', regex: /\bfollow\b/i },
    ],
  },
  {
    intent: 'Compliance',
    patterns: [
      // ── Bug fix: `policies?` only matched "policies", never "policy" ──
      { label: 'policy', regex: /\bpolic(?:y|ies)\b/i },
      { label: 'code of conduct', regex: /\bcode\s+of\s+conduct\b/i },
      { label: 'anti-harassment', regex: /\banti-?harassment\b/i },
      { label: 'guideline', regex: /\bguidelines?\b/i },
      { label: 'standard', regex: /\bstandards?\b/i },
      { label: 'methodology', regex: /\bmethodology\b/i },
      { label: 'mandatory', regex: /\bmandatory\b/i },
      { label: 'regulated', regex: /\bregulated\b/i },
      { label: 'accordance', regex: /\baccordance\b/i },
      { label: 'comply', regex: /\bcompl(?:y|iance|iant)\b/i },
      { label: 'approved', regex: /\bapproved\b/i },
      { label: 'official', regex: /\bofficial\b/i },
      // ── HR / Leave domain ──
      { label: 'leave', regex: /\bleave\b/i },
      { label: 'annual leave', regex: /\bannual\s+leave\b/i },
      { label: 'sick leave', regex: /\bsick\s+leave\b/i },
      { label: 'parental leave', regex: /\bparental\s+leave\b/i },
      { label: 'entitlement', regex: /\bentitlements?\b/i },
      { label: 'employee', regex: /\bemployees?\b/i },
      { label: 'hr', regex: /\bhr\b|\bhuman\s+resources?\b/i },
      { label: 'disciplinary', regex: /\bdisciplinar(?:y|ies)\b/i },
      { label: 'performance review', regex: /\bperformance\s+review\b/i },
      { label: 'workplace', regex: /\bworkplace\b/i },
      { label: 'roster', regex: /\brosters?\b/i },
      { label: 'timesheet', regex: /\btimesheets?\b/i },
    ],
  },
  {
    intent: 'System',
    patterns: [
      { label: 'vpn', regex: /\bvpn\b/i },
      { label: 'password', regex: /\bpassword\b/i },
      { label: 'reset', regex: /\breset\b/i },
      { label: 'expense', regex: /\bexpenses?\b/i },
      { label: 'access', regex: /\baccess\b/i },
      { label: 'portal', regex: /\bportal\b/i },
      { label: 'login', regex: /\b(login|log\s+in)\b/i },
      { label: 'system', regex: /\bsystem\b/i },
      { label: 'tool', regex: /\btool\b/i },
      { label: 'tracking', regex: /\btracking\b/i },
      { label: 'contact', regex: /\bcontact\b/i },
      { label: 'helpdesk', regex: /\bhelpdesk\b/i },
      { label: 'support', regex: /\bsupport\b/i },
    ],
  },
  {
    intent: 'Factual',
    patterns: [
      { label: 'values', regex: /\bvalues\b/i },
      { label: 'who is', regex: /\bwho\s+is\b/i },
      { label: 'what is', regex: /\bwhat\s+is\b/i },
      { label: 'history', regex: /\bhistory\b/i },
      { label: 'leadership', regex: /\bleadership\b/i },
      { label: 'executive', regex: /\bexecutive\b/i },
      { label: 'office', regex: /\boffice\b/i },
      { label: 'company', regex: /\bcompany\b/i },
    ],
  },
];

export function determineIntentDetailed(
  text: string,
  title = '',
): IntentDetail {
  /**
   * Two-pass strategy so the page TITLE always wins over body boilerplate:
   *
   * Pass 1 — title only.
   *   Scans just the title against all categories.
   *   If any category matches → return immediately.
   *   e.g. "Employee Leave Policy" → matches Compliance (policy, leave)
   *        and never reaches the body's "injury" word that would fire Safety.
   *
   * Pass 2 — body text only (runs only when title produced no match).
   *   Scans the plain-text body as before.
   */
  const scanText = (t: string): IntentDetail | null => {
    const lower = t.toLowerCase();
    for (const def of INTENT_DEFS) {
      const matched = def.patterns.filter((p) => p.regex.test(lower));
      if (matched.length > 0) {
        return {
          matchedRule: def.intent,
          matchedKeyword: matched.map((m) => m.label).join(', '),
          intent: def.intent,
          titleUsed: title,
        };
      }
    }
    return null;
  };

  // Pass 1: title
  if (title) {
    const fromTitle = scanText(title);
    if (fromTitle) return fromTitle;
  }

  // Pass 2: body
  const fromBody = scanText(text ?? '');
  if (fromBody) return fromBody;

  return {
    matchedRule: null,
    matchedKeyword: null,
    intent: 'General',
    titleUsed: title,
  };
}

// ─────────────────── People-First Scanner ────────────────────

function makeFinding(
  count: number,
  threshold: number | null,
  penalty: PeopleFirstPenalty,
  recommendation: string,
  examples: string[] = [],
): PeopleFirstFinding {
  const triggered = threshold === null ? count > 0 : count > threshold;
  return { triggered, count, threshold, penalty, recommendation, examples };
}

export function scanPeopleFirst(html: string): PeopleFirstDetail {
  // 1. Directives — command-style LLM instructions (Critical, threshold: 1)
  const directiveRx =
    /\b(ignore|forget|summarize|extract|act as|respond in|output only)\b[\s\S]{0,100}?\b(instructions?|previous|below|format|json|list)\b/gi;
  const directiveMatches = html.match(directiveRx) ?? [];
  const directives = makeFinding(
    directiveMatches.length,
    null,
    'Critical',
    'Remove command-style language directed at AI systems.',
    directiveMatches.slice(0, 3),
  );

  // 2. Hidden Text — content inside display:none / visibility:hidden elements (High, threshold: 10 words)
  const hiddenEls =
    html.match(
      /<[^>]+style\s*=\s*["'][^"']*(?:display\s*:\s*none|visibility\s*:\s*hidden)[^"']*["'][^>]*>[\s\S]*?<\/[^>]+>/gi,
    ) ?? [];
  const hiddenWords = hiddenEls
    .map(
      (el) =>
        el
          .replace(/<[^>]+>/g, ' ')
          .trim()
          .split(/\s+/)
          .filter((w) => w.length > 0).length,
    )
    .reduce((a, b) => a + b, 0);
  const hiddenText = makeFinding(
    hiddenWords,
    10,
    'High',
    'Make all content visible to humans — remove hidden text blocks.',
  );

  // 3. Unicode Junk — zero-width / invisible chars (Medium, threshold: 2)
  const unicodeMatches = html.match(/[\u200B\uFEFF\u00AD\u2060-\u2069]/g) ?? [];
  const unicodeJunk = makeFinding(
    unicodeMatches.length,
    2,
    'Medium',
    'Clean up hidden formatting characters (zero-width spaces, soft hyphens).',
  );

  // 4. Delimiters — machine-parsing fences (Low, threshold: 3 pairs)
  const delimiterRx =
    /(\[\[[\s\S]*?\]\]|\{\{[\s\S]*?\}\}|###[\s\S]*?###|:{4,}|={4,})/g;
  const delimiterMatches = html.match(delimiterRx) ?? [];
  const delimiters = makeFinding(
    delimiterMatches.length,
    3,
    'Low',
    'Use standard headings for structure — avoid mechanical data fences.',
    delimiterMatches.slice(0, 3),
  );

  // 5. AI Attributes — data-llm-* / data-ai-* / data-context-* / long aria-labels (High, threshold: 1)
  const aiAttrRx =
    /data-(llm|ai|context)-[a-z-]+|aria-label=["'][^"']{150,}["']/gi;
  const aiAttrMatches = html.match(aiAttrRx) ?? [];
  const aiAttributes = makeFinding(
    aiAttrMatches.length,
    null,
    'High',
    'Remove non-standard HTML attributes used for AI context injection.',
    aiAttrMatches.slice(0, 3),
  );

  // Overall pass = no Critical or High findings triggered
  const passes =
    !directives.triggered && !hiddenText.triggered && !aiAttributes.triggered;

  return {
    passes,
    directives,
    hiddenText,
    unicodeJunk,
    delimiters,
    aiAttributes,
  };
}

// ─────────────────── Report ────────────────────

export function stripTags(html: string) {
  return (
    (html ?? '')
      // Preserve sentence/paragraph boundaries from common block elements.
      .replace(/<\s*br\s*\/?\s*>/gi, '\n')
      .replace(/<\/(p|div|li|h[1-6]|tr|section|article)>/gi, '$&\n')
      .replace(/<style[\s\S]*?<\/style>/gi, ' ')
      .replace(/<script[\s\S]*?<\/script>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/[ \t]+/g, ' ')
      .replace(/\n{2,}/g, '\n')
      .trim()
  );
}

function extractStructuralChunks(html: string) {
  if (!html)
    return {
      chunks: [],
      summary: { count: 0, oversize: 0, orphanParas: 0, firstPara: '' },
    };
  const paras = (html.match(/<p\b[^>]*>([\s\S]*?)<\/p>/gi) || []).map((p) =>
    stripTags(p),
  );
  const firstPara = paras.length > 0 ? paras[0] : '';
  const oversize = paras.filter((c) => c.split(/\s+/).length > 450).length;
  return {
    chunks: paras,
    summary: {
      count: paras.length,
      oversize,
      orphanParas: paras.length,
      firstPara,
    },
  };
}

export function generateAnalysisReportDetailed(
  html: string,
  text: string,
  item: any,
  computedIntent = '',
  config?: AnalysisConfig,
): AnalysisReportDetail {
  const layoutWarn =
    config?.layoutThresholds?.warning ??
    DEFAULT_ANALYSIS_CONFIG.layoutThresholds.warning;
  const layoutFail =
    config?.layoutThresholds?.fail ??
    DEFAULT_ANALYSIS_CONFIG.layoutThresholds.fail;
  const ttrThreshold =
    config?.keywordStuffing?.ttrThreshold ??
    DEFAULT_ANALYSIS_CONFIG.keywordStuffing.ttrThreshold;
  const ngramMaxCount =
    config?.keywordStuffing?.ngramTopFrequency ??
    DEFAULT_ANALYSIS_CONFIG.keywordStuffing.ngramTopFrequency;
  html = html || '';
  text = text || '';

  const peopleFirstDetail = scanPeopleFirst(html);
  const hasAIPatterns = !peopleFirstDetail.passes;

  const tagCount = (html.match(/<[^>]+>/g) || []).length;
  const textLength = text.length || 1;
  const tagDensity = tagCount / textLength;

  /**
   * SharePoint Modern workbench generates a high volume of structural <div> tags
   * for its grid system, so the density thresholds are driven by admin config:
   *
   *  < layoutWarn*0.63 → Excellent : very clean, text-heavy page
   *  layoutWarn*0.63 – layoutWarn → Standard : balanced page
   *  layoutWarn – layoutFail      → Warning  : "tag soup" — too many nested columns
   *  > layoutFail                 → Fail     : over-engineered layout with very little text
   *
   * (The Excellent/Standard boundary is kept at ~63% of the Warning threshold to
   *  preserve the historical 0.12/0.18/0.25 ratio when defaults are used.)
   */
  type LayoutLevel = 'Excellent' | 'Standard' | 'Warning' | 'Fail';
  const excellentMax = layoutWarn * 0.632; // ≈0.12 at defaults (0.19 × 0.632)
  const standardMax = layoutWarn * 0.947; // ≈0.18 at defaults (0.19 × 0.947)
  const complexLayoutLevel: LayoutLevel =
    tagDensity < excellentMax
      ? 'Excellent'
      : tagDensity <= standardMax
        ? 'Standard'
        : tagDensity <= layoutFail
          ? 'Warning'
          : 'Fail';
  const complexLayout =
    complexLayoutLevel === 'Warning' || complexLayoutLevel === 'Fail';

  const chunkData = extractStructuralChunks(html);
  // Count native + ARIA h2/h3 headings for the clearHeadings check
  const nativeH2H3 = (html.match(/<h[23][^>]*>/gi) || []).length;
  const ariaH2H3 = countAriaHeadingsByLevel(html);
  const h2h3Count = nativeH2H3 + ariaH2H3.h2 + ariaH2H3.h3;

  // Prefer the freshly computed intent over the stale stored item.Intent field.
  // item.Intent reflects the last time the page was saved to SharePoint — it may
  // be "General" even after the page content was updated but not yet re-analysed.
  const effectiveIntent = computedIntent || item.Intent || 'General';
  const missingMetadata = effectiveIntent === 'General';

  const imgTags = html.match(/<img[^>]*>/gi) || [];
  const missingAltText = imgTags.some(
    (img) => !/alt=["']([^"']{2,})["']/i.test(img),
  );

  let hasStrongSummary = false;
  let firstParaWordCount = 0;
  if (chunkData.summary.firstPara) {
    firstParaWordCount = chunkData.summary.firstPara.split(/\s+/).length;
    hasStrongSummary = firstParaWordCount > 10 && firstParaWordCount < 150;
  }

  // ── Keyword Stuffing: TTR + N-gram ──────────────────────────────────────
  //
  // Signal 1 — Type-Token Ratio (TTR)
  //   TTR = unique_words / total_words
  //   Normal prose: 0.55–0.80. Stuffed page: drops below 0.45.
  //
  // Signal 2 — N-gram repetition
  //   Build all bigrams (2-word) and trigrams (3-word), find the most
  //   repeated phrase. If it appears > 3% of all bigrams → stuffed.
  //   Catches "leave policy", "annual leave policy" type stuffing that
  //   single-word frequency completely misses.

  const STOP_WORDS = new Set([
    'this',
    'that',
    'with',
    'from',
    'have',
    'will',
    'been',
    'they',
    'their',
    'when',
    'what',
    'your',
    'also',
    'into',
    'more',
    'than',
    'some',
    'each',
    'about',
    'these',
    'those',
    'were',
    'then',
    'which',
    'there',
    'would',
    'could',
    'should',
    'after',
    'other',
    'very',
    'just',
    'like',
    'over',
  ]);

  const allWords = text
    .toLowerCase()
    .replace(/[^a-z\s]/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 2);
  const contentWords = allWords.filter((w) => !STOP_WORDS.has(w));

  let keywordStuffing = false;
  let topKeyword = '';
  let topKeywordFrequency = 0;
  let ttr = 1;
  let topNgram = '';
  let topNgramFrequency = 0;
  const stuffingSignals: string[] = [];

  if (contentWords.length > 30) {
    // ── Signal 1: TTR ────────────────────────────────────────
    const uniqueWords = new Set(contentWords);
    ttr = Math.round((uniqueWords.size / contentWords.length) * 1000) / 1000;

    // Single top-word (kept for backward compat display)
    const wordCounts: Record<string, number> = {};
    let maxWordCount = 0;
    for (const w of contentWords) {
      wordCounts[w] = (wordCounts[w] || 0) + 1;
      if (wordCounts[w] > maxWordCount) {
        maxWordCount = wordCounts[w];
        topKeyword = w;
      }
    }
    topKeywordFrequency =
      Math.round((maxWordCount / contentWords.length) * 1000) / 10;

    if (ttr < ttrThreshold) {
      stuffingSignals.push(`Low TTR (${ttr} < ${ttrThreshold})`);
    }

    // ── Signal 2: N-gram repetition ──────────────────────────
    const ngramCounts: Record<string, number> = {};
    let totalNgrams = 0;
    // Bigrams
    for (let i = 0; i < allWords.length - 1; i++) {
      if (STOP_WORDS.has(allWords[i]) && STOP_WORDS.has(allWords[i + 1]))
        continue;
      const bg = `${allWords[i]} ${allWords[i + 1]}`;
      ngramCounts[bg] = (ngramCounts[bg] || 0) + 1;
      totalNgrams++;
    }
    // Trigrams
    for (let i = 0; i < allWords.length - 2; i++) {
      const tg = `${allWords[i]} ${allWords[i + 1]} ${allWords[i + 2]}`;
      ngramCounts[tg] = (ngramCounts[tg] || 0) + 1;
    }
    // Find top N-gram
    let maxNgramCount = 0;
    for (const [ng, count] of Object.entries(ngramCounts)) {
      if (count > maxNgramCount) {
        maxNgramCount = count;
        topNgram = ng;
      }
    }
    topNgramFrequency =
      totalNgrams > 0
        ? Math.round((maxNgramCount / totalNgrams) * 1000) / 10
        : 0;

    if (totalNgrams > 0 && maxNgramCount > ngramMaxCount) {
      stuffingSignals.push(
        `Repeated phrase "${topNgram}" appears ${maxNgramCount}× (threshold: ${ngramMaxCount})`,
      );
    }

    keywordStuffing = stuffingSignals.length > 0;
  }

  return {
    writeForPeopleFirst: !hasAIPatterns,
    peopleFirstDetail,
    complexLayoutPenalized: complexLayout,
    complexLayoutLevel,
    shortSections: chunkData.summary.oversize === 0,
    clearHeadings: h2h3Count > 0,
    metadataPresent: !missingMetadata,
    imagesHaveAltText: imgTags.length > 0 ? !missingAltText : true,
    hasStrongSummary,
    noKeywordStuffing: !keywordStuffing,
    metrics: {
      tagCount,
      textLength,
      tagDensity: Math.round(tagDensity * 1000) / 1000,
      oversizeChunks: chunkData.summary.oversize,
      h2h3Count,
      imageCount: imgTags.length,
      firstParaWordCount,
      topKeyword,
      topKeywordFrequency,
      ttr,
      topNgram,
      topNgramFrequency,
      stuffingSignals,
    },
  };
}

// ─────────────────── Full Analysis ────────────────────

export function runFullAnalysis(
  item: any,
  config?: AnalysisConfig,
): FullAnalysisDetail {
  const weights =
    config?.readinessWeights ?? DEFAULT_ANALYSIS_CONFIG.readinessWeights;
  // Mirror what AnalysisWorker does:
  // htmlContent → structure checks (tag counts, alt text, density)
  // plainText   → readability, intent, keyword analysis, embedding
  const html = item.HtmlContent || '';
  const plainText = stripTags(html) || item.Text || '';

  const readability = computeReadabilityDetailed(plainText, config);
  // Page title is declared first — used both by analyzeHtmlStructureDetailed (H1 inference)
  // and determineIntentDetailed (title-first intent matching).
  const pageTitle = (item.Title || item.Name || '').trim();
  const structure = analyzeHtmlStructureDetailed(html, pageTitle);
  const intent = determineIntentDetailed(plainText, pageTitle);
  // Pass the computed intent into the report so metadataPresent uses the
  // live result rather than the stale item.Intent from SharePoint.
  const report = generateAnalysisReportDetailed(
    html,
    plainText,
    item,
    intent.intent,
    config,
  );

  const readiness = Math.min(
    100,
    Math.round(
      structure.score * weights.structure +
        readability.score * weights.readability +
        weights.base,
    ),
  );
  const readinessFormula = `(${structure.score} × ${weights.structure}) + (${readability.score} × ${weights.readability}) + ${weights.base} = ${readiness}`;

  return {
    readability,
    structure,
    intent,
    readiness,
    readinessFormula,
    report,
  };
}

// ─────────────────── AEW & Matrix Evaluation ────────────────────

/**
 * Calculates cosine similarity between two numeric vectors.
 */
export function cosineSimilarity(vecA: number[], vecB: number[]): number {
  let dotProduct = 0;
  let normA = 0;
  let normB = 0;
  for (let i = 0; i < vecA.length; i++) {
    dotProduct += vecA[i] * vecB[i];
    normA += vecA[i] * vecA[i];
    normB += vecB[i] * vecB[i];
  }
  if (normA === 0 || normB === 0) return 0;
  return dotProduct / (Math.sqrt(normA) * Math.sqrt(normB));
}

/**
 * Calculate Average Edge Weight (AEW) from a list of sentence embeddings.
 * AEW measures text coherence by computing the average similarity score
 * of all sentence pairs in a fully connected semantic graph.
 */
export function calculateAEW(sentenceEmbeddings: number[][]): {
  aew: number;
  edgeCount: number;
} {
  if (!sentenceEmbeddings || sentenceEmbeddings.length <= 1) {
    return { aew: 0, edgeCount: 0 };
  }

  let totalWeight = 0;
  let edgeCount = 0;
  const len = sentenceEmbeddings.length;

  for (let i = 0; i < len; i++) {
    for (let j = i + 1; j < len; j++) {
      const sim = cosineSimilarity(
        sentenceEmbeddings[i],
        sentenceEmbeddings[j],
      );
      totalWeight += sim;
      edgeCount++;
    }
  }

  const aew = edgeCount > 0 ? totalWeight / edgeCount : 0;
  return { aew, edgeCount };
}

export type MatrixQuadrant =
  | 'Best Practice'
  | 'Needs Simplification'
  | 'Needs Structure'
  | 'Major Revision';

/**
 * Map AEW and FRE scores to the 2x2 readability matrix quadrant.
 * Thresholds:
 * - Coherence (AEW) High >= 0.40
 * - Readability (FRE) High >= 45
 *
 * Can apply +10 FRE offset if the intent is Procedural/Action Guide and Coherence is High.
 */
export function evaluateMatrixQuadrant(
  aew: number,
  fre: number,
  intent: string,
): {
  quadrant: MatrixQuadrant;
  effectiveFre: number;
  readabilityOffsetApplied: number;
  readabilityThresholdUsed: number;
  coherenceThresholdUsed: number;
} {
  const COHERENCE_HIGH = 0.4;
  const READABILITY_HIGH = 45;

  // Apply offset for Procedural guides if they are highly coherent
  const isProcedural = intent === 'Procedural';
  const canApplyOffset = isProcedural && aew >= COHERENCE_HIGH;

  const readabilityOffsetApplied = canApplyOffset ? 10 : 0;
  const effectiveFre = Math.min(100, fre + readabilityOffsetApplied);

  const isCoherent = aew >= COHERENCE_HIGH;
  const isReadable = effectiveFre >= READABILITY_HIGH;

  let quadrant: MatrixQuadrant = 'Major Revision';
  if (isCoherent && isReadable) quadrant = 'Best Practice';
  else if (isCoherent && !isReadable) quadrant = 'Needs Simplification';
  else if (!isCoherent && isReadable) quadrant = 'Needs Structure';

  return {
    quadrant,
    effectiveFre,
    readabilityOffsetApplied,
    readabilityThresholdUsed: READABILITY_HIGH,
    coherenceThresholdUsed: COHERENCE_HIGH,
  };
}
